import ScheduleType from "../pages/Schedules/ScheduleType";

/** Mirrors backend ScheduleLockPolicy messaging for scheduled slots. */
export const SCHEDULE_LOCK_BLOCKED_BY_VISIT_MESSAGE =
  "Este horário possui visita agendada e não pode ser bloqueado. Cancele a visita antes de bloquear o horário.";

export type ScheduleSlotLike = {
  id: number;
  consultant_id: number;
  date: string;
  status: number;
};

/**
 * Lock is only allowed for free (1) or already-locked (0) slots.
 * Scheduled (2) and canceled (3) must be refused.
 */
export function canLockScheduleStatus(status: number | null | undefined): boolean {
  if (status == null || !Number.isFinite(status)) return false;
  return status === ScheduleType.LOCKED || status === ScheduleType.UNLOCKED;
}

function parseScheduleCoord(coord: string): {
  cellDate: string;
  cellTime: string;
  consultantId: number;
} | null {
  const [datePart, timePart, consultantIdRaw] = coord.split("|");
  if (!datePart || !timePart || consultantIdRaw == null) return null;

  const consultantId = Number.parseInt(consultantIdRaw, 10);
  if (!Number.isFinite(consultantId)) return null;

  return {
    cellDate: datePart,
    cellTime: timePart.substring(0, 5),
    consultantId,
  };
}

function matchSlotToCoord(
  schedule: ScheduleSlotLike,
  coord: string
): boolean {
  const parsed = parseScheduleCoord(coord);
  if (!parsed) return false;

  const [agDateStr, agTimeStr] = schedule.date.replace("T", " ").split(" ");
  const agTime = agTimeStr ? agTimeStr.substring(0, 5) : "";

  return (
    agDateStr === parsed.cellDate &&
    agTime === parsed.cellTime &&
    schedule.consultant_id === parsed.consultantId
  );
}

/** Returns schedule rows in the selection that currently have a visit (status 2). */
export function findScheduledSlotsInSelection(
  dateSelected: string[],
  schedules: ScheduleSlotLike[]
): ScheduleSlotLike[] {
  const scheduled: ScheduleSlotLike[] = [];

  for (const coord of dateSelected) {
    const match = schedules.find(
      (s) =>
        matchSlotToCoord(s, coord) && s.status === ScheduleType.SCHEDULED
    );
    if (match) scheduled.push(match);
  }

  return scheduled;
}

/** Reads `message` or the first `errors` entry from an API error body. */
export function extractApiErrorMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: unknown } })?.response?.data as
    | { message?: unknown; errors?: Record<string, unknown> }
    | undefined;

  if (typeof data?.message === "string" && data.message.trim() !== "") {
    return data.message.trim();
  }

  if (data?.errors && typeof data.errors === "object") {
    for (const value of Object.values(data.errors)) {
      if (typeof value === "string" && value.trim() !== "") {
        return value.trim();
      }
      if (Array.isArray(value) && typeof value[0] === "string") {
        return value[0].trim();
      }
    }
  }

  return null;
}

/** True when the API refused the lock because the slot is not lockable. */
export function isScheduleLockRefusedByPolicy(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 422;
}

/**
 * Message for the "cannot lock" modal: prefers the backend text and only falls
 * back to the canonical policy message when the body carries none.
 */
export function resolveScheduleLockBlockedMessage(error: unknown): string {
  return extractApiErrorMessage(error) ?? SCHEDULE_LOCK_BLOCKED_BY_VISIT_MESSAGE;
}
